// Turns a case's stored siteLocation (a JSON string) into the "confirmed site facts" block given to the model.
// Parsed defensively: a malformed value yields no block rather than failing the assessment.

const MAX_LISTED_NAMES = 8;
/** Mirrors planning-locate-service's EDGE_SLIVER_DEPTH_M, the depth a designation must reach inside the outline to count. */
const EDGE_SLIVER_DEPTH_M = 10;
const MAX_LINE = 300;

function clip(v: unknown): string {
  return String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_LINE);
}

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

const MAX_RING_VERTICES = 400;

interface SiteOutline {
  ring: [number, number][];
  areaHa?: number;
  placement?: string;
  confidence: string;
  documentName?: string;
  userEdited: boolean;
  /** How far the designation search was widened around the outline, to allow for tracing error. */
  toleranceM: number;
}

/** Same rule the Locate screen applies: designations are always searched along the outline, widened more when its placement is less certain. */
function toleranceFor(confidence: string): number {
  return confidence === 'High' ? 10 : confidence === 'Medium' ? 50 : 100;
}

function readOutline(v: unknown): SiteOutline | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const b = v as Record<string, unknown>;
  if (!Array.isArray(b.ring) || b.ring.length < 4 || b.ring.length > MAX_RING_VERTICES) return undefined;
  const ring: [number, number][] = [];
  for (const p of b.ring) {
    if (!Array.isArray(p) || !isNum(p[0]) || !isNum(p[1])) return undefined;
    ring.push([Number(p[0].toFixed(6)), Number(p[1].toFixed(6))]);
  }
  const confidence = clip(b.confidence) || 'Low';
  return {
    ring,
    areaHa: isNum(b.areaHa) && b.areaHa > 0 ? b.areaHa : undefined,
    placement: clip(b.placement) || undefined,
    confidence,
    documentName: clip(b.documentName) || undefined,
    userEdited: b.userEdited === true,
    toleranceM: toleranceFor(confidence),
  };
}

function outlineLines(o: SiteOutline): string[] {
  const area = o.areaHa ? `, about ${o.areaHa.toFixed(o.areaHa < 1 ? 2 : 1)} ha` : '';
  if (o.placement === 'drawn') {
    return [
      `Site outline: drawn on the map by the applicant${area}, so its extent is as they marked it.`,
      `The location above is the centre of the outline. Designations were searched along the outline itself, widened by ${o.toleranceM} m to allow for drawing error.`,
    ];
  }
  const source = o.documentName ? `traced from the Location Plan "${o.documentName}"` : 'traced from the Location Plan supplied with the application';
  const placed = o.placement === 'matched'
    ? 'positioned by matching the plan to Ordnance Survey mapping'
    : 'positioned only by the address, since the plan could not be matched to the map';
  const lines = [`Site outline: ${source}${area}; ${placed}; confidence in the outline's position: ${o.confidence}.`];
  if (o.userEdited) lines.push('The user then moved the outline on the map, so its position is as they set it.');
  lines.push(`The location above is the centre of the outline. Designations were searched along the outline itself, widened by ${o.toleranceM} m to allow for tracing error.`);
  return lines;
}

/** Below this share of the outline, a marginal overlap is the outline touching the designation's edge, not land the site occupies. */
const GRAZING_PCT = 0.5;
/** An overlap not flagged marginal but this small rounds to 0.0%: the same touch at the edge. */
const ZERO_PCT = 0.05;

interface LineWording {
  /** How the site outline is named: traced from a plan, or drawn by the applicant. */
  outline: string;
  /** What may override a marginal overlap figure. */
  authority: string;
}

function constraintLine(c: Record<string, unknown>, generalArea: boolean, w: LineWording): string | undefined {
  const label = clip(c.label) || clip(c.dataset);
  if (!label) return undefined;
  const share = isNum(c.coveragePct) && c.coveragePct >= 0 && c.coveragePct <= 100 ? c.coveragePct : undefined;
  // "Not established" for an overlap that rounds to nothing reads to the model as "might be in it", so a grazing touch is
  // stated as absent, the same as an edge sliver.
  if (c.status === 'Present' && share !== undefined && share < (c.edgeOnly === true ? GRAZING_PCT : ZERO_PCT)) {
    return `- ${label}: not present on the site. ${w.outline} only touches the edge of ${label} (about ${share.toFixed(1)}% of the outline), which is a mapping overlap and not land the site occupies. Do not treat the site as being in, or affecting, ${label}.`;
  }
  if (c.status === 'Present' && c.edgeOnly === true && share !== undefined) {
    return `- ${label}: marginal overlap only — about ${share.toFixed(1)}% of ${w.outline.toLowerCase()} overlaps it, which is within the placement error of the outline (a small shift of the outline would clear it), so it is not established that the site is within ${label}. Do not treat the site as being in, or affected by, ${label} unless ${w.authority} says it is. If it does, that takes precedence over this figure.`;
  }
  if (c.status === 'Present' && c.edgeOnly === true) {
    return `- ${label}: not present on the site. Only a sliver along the edge of the search area was found, with none reaching ${EDGE_SLIVER_DEPTH_M} m inside ${w.outline.toLowerCase()}, which is a mapping overlap and not land the site occupies. Do not treat the site as being in, or affecting, ${label}.`;
  }
  if (c.status === 'Present' && generalArea) {
    const names = Array.isArray(c.names) ? c.names.map(clip).filter(Boolean).slice(0, MAX_LISTED_NAMES) : [];
    const count = isNum(c.count) ? c.count : 0;
    const more = count > names.length && names.length > 0 ? ` (${count} in total)` : '';
    return `- ${label}: in the general area${names.length ? ` — ${names.join('; ')}${more}` : count ? ` — ${count} found` : ''} (not established as being on the site)`;
  }
  if (c.status === 'Present') {
    const names = Array.isArray(c.names) ? c.names.map(clip).filter(Boolean).slice(0, MAX_LISTED_NAMES) : [];
    const count = isNum(c.count) ? c.count : 0;
    const more = count > names.length && names.length > 0 ? ` (${count} in total)` : '';
    const covers = share !== undefined ? `${names.length || count ? '; ' : ' — '}covers about ${share.toFixed(1)}% of ${w.outline.toLowerCase()}` : '';
    return `- ${label}: PRESENT${names.length ? ` — ${names.join('; ')}${more}` : count ? ` — ${count} found` : ''}${covers}`;
  }
  if (c.status === 'Absent') {
    if (c.patchy === true) return `- ${label}: none recorded (local planning authorities supply this data patchily, so this does not confirm there is none)`;
    return generalArea ? `- ${label}: none found in the general area` : `- ${label}: not present`;
  }
  const reason = clip(c.reason);
  return `- ${label}: NOT CHECKED${reason ? ` (${reason})` : ''}`;
}

const TRANSPORT_GROUP = 'Transport';

function isTransport(c: unknown): c is Record<string, unknown> {
  return !!c && typeof c === 'object' && (c as Record<string, unknown>).group === TRANSPORT_GROUP;
}

const NEARBY_PREFIX = 'heritage-nearby-';
const NEARBY_NAMES = 10;

/** Heritage assets near, not on, the site, from the locate service's 500 m search. */
function isNearbyHeritage(c: unknown): c is Record<string, unknown> {
  return !!c && typeof c === 'object' && typeof (c as Record<string, unknown>).dataset === 'string' && ((c as Record<string, unknown>).dataset as string).startsWith(NEARBY_PREFIX);
}

function nearbyLine(c: Record<string, unknown>): string | undefined {
  const label = clip(c.label) || clip(c.dataset);
  if (!label) return undefined;
  if (c.status === 'Not checked') return `- ${label}: NOT CHECKED${clip(c.reason) ? ` (${clip(c.reason)})` : ''}`;
  if (c.status !== 'Present') return `- ${label}: none within 500 m`;
  const count = isNum(c.count) ? c.count : 0;
  const listed = (Array.isArray(c.features) ? c.features : [])
    .filter((f): f is Record<string, unknown> => !!f && typeof f === 'object' && isNum((f as Record<string, unknown>).distanceM))
    .slice(0, NEARBY_NAMES)
    .map((f) => `${clip(f.name)} (${[clip(f.detail), metres(f.distanceM as number)].filter(Boolean).join(', ')})`);
  const more = count > listed.length ? `; and ${count - listed.length} more` : '';
  const atLeast = clip(c.reason) ? 'at least ' : '';
  return `- ${label}: ${atLeast}${count} within 500 m${listed.length ? ` — ${listed.join('; ')}${more}` : ''}`;
}

function metres(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

const FIGURE_KEYS = ['weekday', 'daytime', 'peakHour', 'services', 'saturday', 'sunday'] as const;

type Figures = Record<(typeof FIGURE_KEYS)[number], number>;

function figuresOf(v: unknown): Figures | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const r = v as Record<string, unknown>;
  const out: Partial<Figures> = {};
  for (const k of FIGURE_KEYS) {
    const n = r[k];
    if (!isNum(n) || n < 0) return undefined;
    out[k] = n;
  }
  return out as Figures;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function figuresText(f: Figures): string {
  if (f.weekday === 0 && f.saturday === 0 && f.sunday === 0) return 'no scheduled departures';
  return `${plural(f.weekday, 'bus', 'buses')} on a weekday (${f.peakHour} in the busiest hour between 07:00 and 19:00, ${plural(f.services, 'route', 'routes')}), ${f.saturday} on Saturday, ${f.sunday} on Sunday`;
}

/** Scheduled bus frequency for the bus-stop row, as one clause per line; empty when the index carries no timetable data. */
function busServiceLines(c: Record<string, unknown>): string[] {
  const b = c.busService;
  if (!b || typeof b !== 'object') return [];
  const r = b as Record<string, unknown>;
  const date = typeof r.timetableDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.timetableDate) ? r.timetableDate : undefined;
  if (!date) return [];
  const lines: string[] = [];
  if (r.nearest === 'unknown') {
    lines.push('  Bus frequency at the nearest stop: unknown, as the published timetable data does not cover it (it covers England only, and some operators are missing). Do not infer that there is no service.');
  } else if (r.nearest === 'none') {
    lines.push(`  Bus frequency at the nearest stop: no scheduled departures in the published timetables (reference Wednesday ${date}).`);
  } else {
    const f = figuresOf(r.nearest);
    if (f) lines.push(`  Bus frequency at the nearest stop (scheduled departures, reference Wednesday ${date}): ${figuresText(f)}.`);
  }
  const best = figuresOf(r.best);
  const bestRaw = r.best as Record<string, unknown> | undefined;
  if (best && bestRaw) {
    const name = clip(bestRaw.name);
    const away = isNum(bestRaw.distanceM) ? `, about ${metres(bestRaw.distanceM)} away` : '';
    if (name) lines.push(`  A better-served stop within walking distance: ${name}${away}: ${figuresText(best)}.`);
  }
  return lines;
}

function hasBusService(c: unknown): boolean {
  return !!c && typeof c === 'object' && busServiceLines(c as Record<string, unknown>).length > 0;
}

function transportLine(c: Record<string, unknown>): string | undefined {
  const label = clip(c.label) || clip(c.dataset);
  if (!label) return undefined;
  if (c.status === 'Present') {
    const name = Array.isArray(c.names) ? clip(c.names[0]) : '';
    const fromBoundary = c.measuredFrom === 'boundary';
    let away = '';
    if (isNum(c.distanceM)) {
      if (fromBoundary) {
        const centre = isNum(c.centreDistanceM) ? ` (${metres(c.centreDistanceM)} from its centre)` : '';
        away = c.distanceM === 0 ? `, inside the site boundary${centre}` : `, about ${metres(c.distanceM)} from the site boundary${centre}`;
      } else {
        away = `, about ${metres(c.distanceM)} from the pin`;
      }
    }
    const within = isNum(c.within400M) && isNum(c.within800M)
      ? `; ${c.within400M} within 400 m and ${c.within800M} within 800 m${fromBoundary ? ' of the boundary' : ''}`
      : '';
    return [`- ${label}: ${name || 'recorded'}${away}${within}`, ...busServiceLines(c)].join('\n');
  }
  if (c.status === 'Absent') return `- ${label}: none recorded within the search distance`;
  const reason = clip(c.reason);
  return `- ${label}: NOT CHECKED${reason ? ` (${reason})` : ''}`;
}

export function buildSiteFacts(raw: unknown, opts: { preApplication?: boolean } = {}): string | undefined {
  if (raw == null || raw === '') return undefined;
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const s = value as Record<string, unknown>;
  if (!isNum(s.lat) || !isNum(s.lon)) return undefined;

  const outline = readOutline(s.boundary);
  const radius = isNum(s.radiusM) ? Math.round(s.radiusM) : undefined;
  const confidence = clip(s.confidence) || 'Unknown';
  const how = opts.preApplication ? 'marked on the map by the applicant'
    : s.userEdited === true ? 'derived from the application documents, then adjusted by the user' : 'derived from the application documents';
  const lines: string[] = [
    '## Confirmed site facts',
    `Location: ${s.lat.toFixed(5)}, ${s.lon.toFixed(5)} (WGS84), ${how}. Confidence in the location: ${confidence}.`,
  ];
  if (outline) lines.push(...outlineLines(outline));
  else if (opts.preApplication) {
    lines.push("The applicant marked only a point, not an outline, so the site's extent is unknown. Treat the designations below as the general area around the site, not as certainly on it, unless the applicant's description says the site is in one.");
    if (radius) lines.push(`Search area: the site is taken to lie within about ${radius} m of this point.`);
  } else {
    lines.push("No Location Plan outline was traced, so the site's extent is unknown and this analysis rests on the planning statement alone. The location is only an approximate point taken from the text. The planning statement takes precedence over everything below: where it says the site is in, or affected by, Green Belt or any other locational designation or area, regard that as fact, whether or not it appears below.");
    if (radius) lines.push(`Search area: the site is taken to lie within about ${radius} m of this point.`);
  }

  const evidence = Array.isArray(s.evidence) ? s.evidence.map(clip).filter(Boolean).slice(0, 6) : [];
  if (evidence.length) lines.push('How the location was established:', ...evidence.map((e) => `- ${e}`));
  const warnings = Array.isArray(s.warnings) ? s.warnings.map(clip).filter(Boolean).slice(0, 6) : [];
  if (warnings.length) lines.push('Caveats:', ...warnings.map((w) => `- ${w}`));

  // No date here: the block is part of what the assessment cache keys on, and a check date would stop identical results
  // matching on another day. Results that differ change the designation lines themselves, and so the key.
  const wording: LineWording = {
    outline: outline?.placement === 'drawn' ? 'The outline the applicant drew' : 'The traced outline',
    authority: opts.preApplication ? "the applicant's description of the proposal" : 'the planning statement or the application documents',
  };
  const all = Array.isArray(s.constraints) ? s.constraints : [];
  const transportRows = all.filter(isTransport);
  const transport = transportRows.map(transportLine).filter((l): l is string => !!l);
  const transportBasis = transportRows.some((c) => c.measuredFrom === 'boundary')
    ? 'measured from the nearest point of the site boundary, with the distance from the site centre also given'
    : 'measured from the pin (no site outline has been traced)';
  const nearbyRows = all.filter(isNearbyHeritage);
  const nearby = nearbyRows.map(nearbyLine).filter((l): l is string => !!l);
  const constraints = all
    .filter((c) => !isTransport(c) && !isNearbyHeritage(c))
    .map((c) => (c && typeof c === 'object' ? constraintLine(c as Record<string, unknown>, !outline, wording) : undefined))
    .filter((l): l is string => !!l);
  if (constraints.length) {
    lines.push(
      `Designations from planning.data.gov.uk, Natural England and the Mining Remediation Authority, indicative only, searched ${outline ? 'along the site outline' : `within about ${radius ?? 'a few hundred'} m of the resolved location, which describes the general area and not necessarily the site itself`}:`,
      ...constraints,
    );
  }
  if (nearby.length) {
    const from = nearbyRows.some((c) => c.measuredFrom === 'boundary') ? 'the nearest point of the site boundary' : 'the resolved location (no site outline has been traced)';
    lines.push(
      `Designated heritage assets near the site but not on it (planning.data.gov.uk, including Historic England data; within 500 m, straight-line distances from ${from}). Development can affect the significance of a heritage asset through its setting, so consider whether the proposal would affect the setting of any of these (the duty in section 66 of the Planning (Listed Buildings and Conservation Areas) Act 1990 for listed buildings, and NPPF chapter 16). Distance alone does not establish an effect on setting; intervening development, topography and views matter:`,
      ...nearby,
    );
  }
  if (transport.length) {
    const withFrequency = transportRows.some(hasBusService);
    const caveat = withFrequency
      ? 'Bus frequencies are scheduled departures taken from the Bus Open Data Service timetables for a typical Wednesday, Saturday and Sunday: they are not a guarantee of service, can change when operators revise timetables, and do not describe quality or reliability. Rail and tram rows are locations only. None of this is a planning designation:'
      : 'These are recorded stops and stations, not a statement of service frequency or quality, and they are not planning designations:';
    lines.push(
      `Public transport near the site (NaPTAN and Bus Open Data Service, Department for Transport; straight-line distances, ${transportBasis}). ${caveat}`,
      ...transport,
    );
  }
  return lines.join('\n');
}

const MAP_NAMES = 8;
const MAP_PLACES = 30;

/** Stop positions a transport map draws; the report needs nothing else about them. */
function mapPlaces(raw: unknown): { name: string; lat: number; lon: number; distanceM: number }[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object' && isNum((p as Record<string, unknown>).lat) && isNum((p as Record<string, unknown>).lon) && isNum((p as Record<string, unknown>).distanceM))
    .map((p) => ({ name: clip(p.name), lat: p.lat as number, lon: p.lon as number, distanceM: Math.round(p.distanceM as number) }))
    .filter((p) => p.name)
    .slice(0, MAP_PLACES);
}

/**
 * A compact copy of the confirmed site location kept on the assessment so its report can draw maps of the
 * designations found. Only what a map needs: the point, the search radius, the site outline (when one was traced)
 * and the Present designations and transport stops.
 * Absent and unchecked results are left out, and so is any link to the case (an assessment stores none).
 */
export function buildSiteMap(raw: unknown): string | undefined {
  if (raw == null || raw === '') return undefined;
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const s = value as Record<string, unknown>;
  if (!isNum(s.lat) || !isNum(s.lon)) return undefined;
  const constraints = (Array.isArray(s.constraints) ? s.constraints : [])
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object' && (c as Record<string, unknown>).status === 'Present' && (c as Record<string, unknown>).edgeOnly !== true && !isNearbyHeritage(c))
    .map((c) => ({
      dataset: clip(c.dataset),
      label: clip(c.label),
      status: 'Present',
      count: isNum(c.count) ? c.count : 0,
      names: Array.isArray(c.names) ? c.names.map(clip).filter(Boolean).slice(0, MAP_NAMES) : [],
      ...(isTransport(c) ? { group: TRANSPORT_GROUP, places: mapPlaces(c.places) } : {}),
    }))
    .filter((c) => c.dataset);
  if (constraints.length === 0) return undefined;
  const outline = readOutline(s.boundary);
  return JSON.stringify({
    lat: s.lat, lon: s.lon, radiusM: isNum(s.radiusM) ? Math.round(s.radiusM) : 400, constraints,
    ...(outline ? { boundary: { ring: outline.ring, confidence: outline.confidence, toleranceM: outline.toleranceM, userEdited: outline.userEdited, ...(outline.areaHa ? { areaHa: outline.areaHa } : {}) } } : {}),
  });
}
